import React, { useEffect, useMemo, useState } from 'react';

import useReduxState from '@portal/hooks/useReduxState';

import { PaymentAccount } from '@portal/models/module';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { translate } from '@portal/services/i18n';
import { makeStyles } from '@material-ui/core/styles';
import { WL_COMPANY_PRIMARY_COLOR } from '@portal/config/env';
import { PaymentAccountActions } from '@portal/actions';
import { useDispatch } from 'react-redux';
import {
  formatPaymentAccountForSelect,
  getApprovedPaymentAccountsByTypeAsSelect,
} from '@portal/utils/paymentAccount';
import { PaymentAccountType } from '@portal/models/enumerators/paymentAccount';

export const useStyles = (sideMenuInput: boolean) => {
  const lightColor = sideMenuInput
    ? '@text-on-sidebar !important'
    : WL_COMPANY_PRIMARY_COLOR;
  const highlightColor = sideMenuInput
    ? WL_COMPANY_PRIMARY_COLOR
    : '@text-on-sidebar !important';

  return makeStyles(() => ({
    paper: {
      backgroundColor: '@text-on-sidebar !important',
      minWidth: '240px',
    },
    menuItem: {
      '&:hover': {
        color: highlightColor,
      },
      '&:selected': {
        color: lightColor,
        backgroundColor: highlightColor,
      },
    },
  }));
};

type PayAccSelectInterface = {
  value: string;
  data: PaymentAccount;
  name?: string | null;
};

const PaymentAccountSelector = ({
  sideMenuInput = false,
}: {
  sideMenuInput?: boolean;
}) => {
  const classes = useStyles(sideMenuInput);
  const temp = classes();
  const dispatch = useDispatch();

  const {
    user: { me: loggedUser },
    loading: { load },
    paymentAccount: { selectedPaymentAccount },
  } = useReduxState();

  const [currentPaymentAcc, setCurrentPaymentAcc] = useState(
    selectedPaymentAccount
      ? formatPaymentAccountForSelect(selectedPaymentAccount)
      : null
  );

  const accItems = useMemo(
    () =>
      getApprovedPaymentAccountsByTypeAsSelect(loggedUser).map((account) => {
        if (account.data.type === PaymentAccountType.ACCOUNT) {
          const normalizedAccount = {
            value: account.value,
            data: account.data,
            name: translate('COMPONENTS.PAYMENT_ACCOUNT_SELECTOR.PRIMARY'),
          };

          return normalizedAccount;
        }

        return account;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loggedUser.account.paymentAccounts]
  );

  const handleSelect = ({ target: { value } }) => {
    const accItemPay = accItems.find((item) => item.value === value);
    if (accItemPay) {
      setCurrentPaymentAcc(accItemPay);
      dispatch(PaymentAccountActions.changePaymentAccount(accItemPay.data));
    }
  };

  useEffect(() => {
    if (
      (selectedPaymentAccount && !currentPaymentAcc) ||
      (selectedPaymentAccount &&
        currentPaymentAcc &&
        selectedPaymentAccount.id !== currentPaymentAcc.value)
    ) {
      setCurrentPaymentAcc(
        formatPaymentAccountForSelect(selectedPaymentAccount)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPaymentAccount]);

  return accItems && accItems.length > 1 ? (
    <div
      className={
        sideMenuInput
          ? 'payment-account-selector-side-menu'
          : 'payment-account-selector'
      }
    >
      <FormControl variant="outlined">
        <InputLabel>
          {translate('COMPONENTS.PAYMENT_ACCOUNT_SELECTOR.LABEL')}
        </InputLabel>
        <Select
          MenuProps={{
            classes: {
              paper: temp.paper,
            },
          }}
          value={currentPaymentAcc?.value || ''}
          onChange={(e) => handleSelect(e)}
          autoWidth
          label={translate('COMPONENTS.PAYMENT_ACCOUNT_SELECTOR.LABEL')}
          readOnly={accItems.length === 1}
          disabled={load}
        >
          {accItems.map((item: PayAccSelectInterface, itemIndex: number) => (
            <MenuItem
              value={item.value}
              key={itemIndex.toString()}
              className={temp.menuItem}
            >
              {item.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  ) : null;
};

export default PaymentAccountSelector;
